import { CRMAccountSettings } from '@alucio/aws-beacon-amplify/src/models';
import { isIOS, isTablet } from 'react-device-detect';

import * as logger from 'src/utils/logger';

import { CRMAccount } from './CRMIndexedDBTypes';

export const CRM_ACCOUNT_SEARCH_RESULT_LIMIT = 50;
export const CRM_ACCOUNT_SEARCH_CANDIDATE_LIMIT = CRM_ACCOUNT_SEARCH_RESULT_LIMIT * 4;
export const CRM_ACCOUNT_SEARCH_MAX_TOKENS_PER_ACCOUNT = 24;
export const CRM_ACCOUNT_SEARCH_INDEX_CHUNK_SIZE = 100;
export const CRM_ACCOUNT_SEARCH_TOKEN_PREFIX_LIMIT = 900;
export const CRM_ACCOUNT_SEARCH_TOKEN_INDEX_LENGTH = 48;
export const CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_ID = 'ACCOUNT_SEARCH_BACKFILL_V2';
export const CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_VALUE = 'complete';

export type CRMAccountSearchToken = {
  id: string;
  token: string;
  accountId: string;
};

const ID_LIKE_TOKEN_REGEX = /^v?(?=[a-z0-9]{12,}$)(?=[a-z0-9]*\d)[a-z0-9]+$/i;
const ADDRESS_KEY_REGEX = /address|addresses/i;
const SEARCHABLE_ACCOUNT_KEY_REGEX = /name|city|state|street|postal|zip|country|province|territory/i;
const SKIPPED_ACCOUNT_KEY_REGEX = /base64|thumbnail|image|photo|blob|id$/i;

export const isIOSCRMAccountLocalSearch = () => isIOS && isTablet;

export const normalizeCRMAccountSearchToken = (value: unknown): string => (
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
);

export const tokenizeCRMAccountSearchText = (value: unknown): string[] => (
  normalizeCRMAccountSearchToken(value)
    .split(/[^a-z0-9]+/i)
    .filter(token => token.length >= 2)
);

const pushSearchValue = (values: string[], value: unknown) => {
  if (value != null && typeof value !== 'object') {
    values.push(String(value));
  }
}

const collectSearchableAccountValues = (
  value: unknown,
  values: string[],
  depth = 0,
  insideAddress = false,
) => {
  if (!value || depth > 4) return;

  if (Array.isArray(value)) {
    value.slice(0, 10).forEach(item => collectSearchableAccountValues(item, values, depth + 1, insideAddress));
    return;
  }

  if (typeof value !== 'object') return;

  Object.entries(value).forEach(([key, nestedValue]) => {
    if (SKIPPED_ACCOUNT_KEY_REGEX.test(key)) {
      return;
    }

    const isAddressKey = ADDRESS_KEY_REGEX.test(key);
    const isSearchableKey = SEARCHABLE_ACCOUNT_KEY_REGEX.test(key);

    if (isSearchableKey || insideAddress) {
      pushSearchValue(values, nestedValue);
    }

    collectSearchableAccountValues(nestedValue, values, depth + 1, insideAddress || isAddressKey);
  });
}

const getFormattedCRMSearchValue = (model: unknown, format: string): string => (
  format
    .replace(/\${(.*?)}/g, (_, path) => {
      const value = String(path).split('.').reduce<unknown>((acc, key) => (
        acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[key] : ''
      ), model);
      return String(value || '');
    })
    .replace(/^[\s.,]+/, '')
);

export const buildCRMAccountSearchText = (
  account: CRMAccount,
  config?: CRMAccountSettings | null,
): string => {
  const values: string[] = [];

  pushSearchValue(values, account.Name);
  pushSearchValue(values, account.name);
  pushSearchValue(values, account.FirstName);
  pushSearchValue(values, account.firstName);
  pushSearchValue(values, account.firstname);
  pushSearchValue(values, account.LastName);
  pushSearchValue(values, account.lastName);
  pushSearchValue(values, account.lastname);

  try {
    if (config?.displaySettings) {
      pushSearchValue(values, getFormattedCRMSearchValue(account, config.displaySettings.shortened));
    }
  } catch (e) {
    logger.CRMMachine.warn('Unable to format CRM account search text', { error: e });
  }

  collectSearchableAccountValues(account, values);

  return values.join(' ').toLowerCase();
}

export const buildCRMAccountSearchRows = (
  account: CRMAccount,
  config?: CRMAccountSettings | null,
): CRMAccountSearchToken[] => {
  const searchText = buildCRMAccountSearchText(account, config);
  const tokens = new Set<string>();

  tokenizeCRMAccountSearchText(searchText).forEach((token) => {
    if (!ID_LIKE_TOKEN_REGEX.test(token)) {
      tokens.add(token.slice(0, CRM_ACCOUNT_SEARCH_TOKEN_INDEX_LENGTH));
    }
  });

  return Array.from(tokens)
    .slice(0, CRM_ACCOUNT_SEARCH_MAX_TOKENS_PER_ACCOUNT)
    .map(token => ({
      id: `${token}|${account.id}`,
      token,
      accountId: account.id,
    }));
}
